import React, { useState, useRef, useEffect } from "react";
import { PanelLeftClose, PanelLeftOpen, RotateCcw, Filter, ChevronDown, Check, X, Search as SearchIcon } from "lucide-react";

/**
 * MultiSelectDropdown — dropdown compatto con multi-select, chip attivi e search interna.
 */
function MultiSelectDropdown({ sectionId, options, selected, onToggle, onClear, entity, placeholder }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const filtered = q
    ? options.filter(o => (o.label || "").toLowerCase().includes(q.toLowerCase()))
    : options;
  const count = selected.length;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid={`side-filter-${entity}-${sectionId}-dropdown`}
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center gap-1.5 px-2 py-1.5 text-xs border transition-colors ${
          count > 0
            ? "border-[#155E75] bg-[#155E75]/5 text-slate-800"
            : "border-slate-300 bg-white text-slate-600 hover:border-slate-400"
        }`}
      >
        <span className="flex-1 truncate text-left">
          {count === 0
            ? (placeholder || "Seleziona…")
            : count === 1
              ? (options.find(o => o.value === selected[0])?.label || selected[0])
              : <><span className="font-bold text-[#155E75]">{count}</span> selezionati</>
          }
        </span>
        {count > 0 && (
          <span onClick={(e) => { e.stopPropagation(); onClear(); }}
                className="p-0.5 rounded hover:bg-slate-200 cursor-pointer"
                title="Pulisci">
            <X className="w-2.5 h-2.5" />
          </span>
        )}
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={1.6} />
      </button>

      {open && (
        <div
          data-testid={`side-filter-${entity}-${sectionId}-menu`}
          className="absolute left-0 right-0 mt-1 z-40 bg-white border border-white/[0.04] shadow-lg rounded-lg max-h-64 overflow-hidden flex flex-col"
        >
          {options.length > 6 && (
            <div className="border-b border-slate-200 px-2 py-1.5 flex items-center gap-1.5">
              <SearchIcon className="w-3 h-3 text-slate-400" strokeWidth={1.5} />
              <input
                value={q} onChange={e => setQ(e.target.value)}
                placeholder="Cerca…"
                className="flex-1 text-xs text-slate-800 focus:outline-none placeholder:text-slate-400"
              />
            </div>
          )}
          <div className="flex-1 overflow-y-auto py-0.5">
            {filtered.length === 0 && (
              <p className="px-3 py-2 text-[11px] text-slate-400 italic">Nessun risultato</p>
            )}
            {filtered.map(opt => {
              const on = selected.includes(opt.value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  data-testid={`side-filter-${entity}-${sectionId}-opt-${String(opt.value).replace(/\s+/g, "-")}`}
                  onClick={() => onToggle(opt.value)}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 text-xs text-left transition-colors group ${
                    on ? "bg-[#155E75]/8 text-slate-900 rounded" : "hover:bg-[#155E75] hover:text-white text-slate-700 rounded"
                  }`}
                >
                  <span className={`w-3 h-3 border rounded-sm flex items-center justify-center flex-shrink-0 ${
                    on ? "bg-[#155E75] border-[#155E75]" : "border-slate-400 bg-white group-hover:border-white"
                  }`}>
                    {on && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
                  </span>
                  <span className="flex-1 truncate">{opt.label}</span>
                  {opt.count !== undefined && (
                    <span className={`text-[10px] tabular-nums ${on ? "text-[#155E75] font-bold" : "text-slate-400 group-hover:text-white"}`}>{opt.count}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Chips attive */}
      {count > 0 && count <= 3 && (
        <div className="flex flex-wrap gap-1 mt-1">
          {selected.map(v => {
            const opt = options.find(o => o.value === v);
            if (!opt) return null;
            return (
              <span key={v}
                onClick={() => onToggle(v)}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[#155E75]/8 text-[#155E75] text-[10px] border border-[#155E75]/40 cursor-pointer hover:bg-[#155E75]/15">
                {opt.label} <X className="w-2 h-2" />
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * SideFilters — Pannello filtri laterale collassabile.
 * Layout compatto con dropdown multi-select invece di lunghe liste checkbox.
 */
export default function SideFilters({ open, setOpen, filters, setFilters, sections, totalMatch, totalAll, entity = "items" }) {
  const set = (id, v) => setFilters(prev => ({ ...prev, [id]: v }));

  const toggleValue = (sectionId, value) => {
    const cur = filters[sectionId] || [];
    const next = cur.includes(value) ? cur.filter(v => v !== value) : [...cur, value];
    set(sectionId, next);
  };
  const clearSection = (sectionId) => set(sectionId, []);

  const reset = () => setFilters({});
  const activeCount = Object.entries(filters).reduce((acc, [, v]) => {
    if (Array.isArray(v)) return acc + v.length;
    if (typeof v === "string" && v.trim()) return acc + 1;
    if (v === true) return acc + 1;
    return acc;
  }, 0);

  // Collapsed rail
  if (!open) {
    return (
      <aside
        data-testid="side-filters-collapsed"
        className="flex-shrink-0 w-full lg:w-11 border border-white/[0.04] rounded-lg bg-[#F8FAFC] flex lg:flex-col items-center justify-between lg:justify-start py-2 lg:py-3 px-3 lg:px-0 gap-2 self-start lg:sticky lg:top-4"
      >
        <button
          onClick={() => setOpen(true)}
          data-testid="side-filters-expand"
          className="w-8 h-8 rounded flex items-center justify-center text-slate-700 hover:bg-slate-200 transition-colors"
          title="Mostra filtri"
        >
          <PanelLeftOpen className="w-4 h-4" strokeWidth={1.6} />
        </button>
        <span className="text-[10px] uppercase tracking-widest font-bold text-slate-600 lg:hidden">Filtri</span>
        {activeCount > 0 && (
          <span className="text-[9px] font-bold bg-[#155E75] text-white px-1 py-0.5 tracking-widest tabular-nums rounded">{activeCount}</span>
        )}
        <div className="hidden lg:flex mt-2 flex-col items-center gap-3 text-[#94A3B8]">
          {sections.slice(0, 6).map(s => s.icon ? <s.icon key={s.id} className="w-3.5 h-3.5" strokeWidth={1.5} /> : null)}
        </div>
      </aside>
    );
  }

  return (
    <aside
      data-testid="side-filters-panel"
      className="flex-shrink-0 w-full lg:w-64 border border-white/[0.04] rounded-lg bg-[#F8FAFC] flex flex-col self-start lg:sticky lg:top-4 max-h-[80vh] lg:max-h-[calc(100vh-2rem)] overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-slate-200 bg-white">
        <Filter className="w-3.5 h-3.5 text-[#155E75]" strokeWidth={1.6} />
        <span className="text-[11px] uppercase tracking-widest font-bold text-slate-800" style={{ fontFamily: "Chivo, sans-serif" }}>
          Filtri
        </span>
        {activeCount > 0 && (
          <span className="text-[9px] font-bold bg-[#155E75] text-white px-1.5 py-0.5 tabular-nums tracking-wider">{activeCount}</span>
        )}
        <button
          onClick={() => setOpen(false)}
          data-testid="side-filters-collapse"
          className="ml-auto p-1 rounded text-slate-500 hover:text-slate-800 transition-colors"
          title="Nascondi filtri"
        >
          <PanelLeftClose className="w-3.5 h-3.5" strokeWidth={1.6} />
        </button>
      </div>

      {/* Match count */}
      <div className="px-3 py-2 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500 bg-white">
        Risultati: <span className="text-slate-900 font-bold tabular-nums">{totalMatch}</span>
        {totalAll !== undefined && totalAll !== totalMatch && (
          <span> / <span className="tabular-nums">{totalAll}</span></span>
        )}
      </div>

      {/* Sections */}
      <div className="flex-1 overflow-y-auto py-2 px-2 space-y-2.5">
        {sections.map(sec => (
          <div key={sec.id}>
            <div className="flex items-center gap-1.5 mb-1">
              {sec.icon && <sec.icon className="w-3 h-3 text-slate-500" strokeWidth={1.5} />}
              <p className="text-[9px] tracking-[0.18em] uppercase font-bold text-slate-500">{sec.label}</p>
            </div>
            {sec.type === "checkbox" && (
              <MultiSelectDropdown
                sectionId={sec.id}
                options={sec.options || []}
                selected={filters[sec.id] || []}
                onToggle={(v) => toggleValue(sec.id, v)}
                onClear={() => clearSection(sec.id)}
                entity={entity}
                placeholder={sec.placeholder || "Tutti"}
              />
            )}
            {sec.type === "text" && (
              <input
                data-testid={`side-filter-${entity}-${sec.id}-input`}
                value={filters[sec.id] || ""}
                onChange={e => set(sec.id, e.target.value)}
                placeholder={sec.placeholder || ""}
                className="w-full bg-white border border-slate-300 rounded-md px-2 py-1.5 text-xs text-slate-800 focus:outline-none focus:border-[#155E75]"
              />
            )}
            {sec.type === "toggle" && (
              <button
                onClick={() => set(sec.id, !filters[sec.id])}
                data-testid={`side-filter-${entity}-${sec.id}-toggle`}
                className={`w-full flex items-center justify-between px-2 py-1.5 text-xs transition-colors border ${
                  filters[sec.id]
                    ? "bg-[#155E75] text-white border-[#155E75] rounded-md"
                    : "bg-white border-slate-300 text-slate-700 hover:border-slate-400 rounded-md"
                }`}
              >
                <span>{sec.toggleLabel || sec.label}</span>
                <span className={`w-6 h-3 relative rounded-full ${filters[sec.id] ? "bg-white/30" : "bg-slate-300"}`}>
                  <span className={`absolute top-0 w-3 h-3 rounded-full bg-white transition-all ${filters[sec.id] ? "right-0" : "left-0"}`} />
                </span>
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Footer actions */}
      <div className="p-2 border-t border-slate-200 flex items-center gap-2 bg-white">
        {activeCount > 0 ? (
          <button
            onClick={reset}
            data-testid={`side-filters-reset`}
            className="flex-1 inline-flex items-center justify-center gap-1 border border-slate-300 rounded-md text-slate-600 hover:text-red-700 hover:border-red-700 px-2 py-1.5 text-[10px] uppercase tracking-widest transition-colors"
          >
            <RotateCcw className="w-3 h-3" />
            Reset ({activeCount})
          </button>
        ) : (
          <p className="text-[9px] uppercase tracking-widest text-slate-400 flex-1">Nessun filtro attivo</p>
        )}
      </div>
    </aside>
  );
}
